import { Injectable } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	distinctUntilChanged,
	interval,
	map,
	shareReplay,
	startWith,
	switchMap,
	tap,
} from 'rxjs';

// --- Services ---
import {
	GuestClientsService,
	type IGetGuestClientsParams,
} from '@services/guest-clients.service';

// --- Stores ---
import {
	GuestClientsStore,
	type IAccessRequestsState,
} from '@stores/guest-clients.store';

// --- Interfaces ---
import type { IGuestClient, IStatusRequest } from '@interfaces/guestClient';

const REFRESH_INTERVAL = 120 * 1000;

@Injectable({
	providedIn: 'root',
})
export class GuestClientsFacade {
	private readonly autoRefresh$ = interval(REFRESH_INTERVAL).pipe(startWith(0));
	private readonly _refresh = new BehaviorSubject(undefined);

	state!: IAccessRequestsState;

	private readonly _filter = new BehaviorSubject<IGetGuestClientsParams>({});
	readonly filter$ = this._filter.asObservable().pipe(distinctUntilChanged());

	private readonly handleRequest$ = combineLatest([
		this.filter$,
		this.autoRefresh$,
		this._refresh.asObservable(),
	]).pipe(
		switchMap(() =>
			this.getGuestClients({ pageLimit: 20, ...this._filter.value })
		)
	);

	readonly guestClientsState$ = combineLatest([
		this.guestClientsStore.guestClientsState$,
		this.handleRequest$,
	]).pipe(
		map(([state]) => {
			this.state = state;
			return state;
		}),
		shareReplay({ refCount: true })
	);

	constructor(
		private guestClientsService: GuestClientsService,
		private guestClientsStore: GuestClientsStore
	) {}

	getSettings() {
		return this.guestClientsService.getSettings();
	}

	validateEmail(email: string) {
		return this.guestClientsService
			.validateEmail({ email })
			.pipe(shareReplay());
	}

	getGuestClients(queryParams?: IGetGuestClientsParams) {
		return this.guestClientsService
			.getGuestClients(queryParams)
			.pipe(
				tap(guestClients =>
					this.guestClientsStore.updateGuestClients(guestClients)
				)
			);
	}

	newClient(guestClient: IGuestClient) {
		return this.guestClientsService.postClient(guestClient);
	}

	updateStatus(id: IGuestClient['id'], status: IStatusRequest) {
		return this.guestClientsService
			.updateStatus(id, status)
			.pipe(
				tap(guestClient =>
					this.guestClientsStore.updateGuestClient(guestClient)
				)
			);
	}

	filterGuestClients(filter: IGetGuestClientsParams) {
		this._filter.next({ ...this._filter.value, ...filter });
	}
}
